import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useHistory, Link } from 'react-router-dom';
import {
  Button,
  Card,
  Checkbox,
  Grid,
  Icon,
  Typography,
  TextField,
  IconButton,
  FormControl,
  FormHelperText
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { Breadcrumb, BackdropCircular } from 'components';
import logo from 'components/Logo/logo-dermpro.svg';
import {
  getMasterCatalogProductsApi,
  addTasksToQueueApi,
  getStoreProductsApi,
  getBrands,
  sendRequestProductsEmailApi,
  getStoreTaxClassesApi
} from 'api';
import { imgBaseUrl } from 'configs';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';


const useStyles = makeStyles(() => ({
  disabled: {
    filter: 'opacity(0.5) grayscale(0.9)',
  }
}));

export default function BrowseDirectFulfillmentProducts() {
  const classes = useStyles();
  const params = useParams<{id: string}>();
  const dispatch = useDispatch();
  const history = useHistory();
  const galleryData = useSelector((state: IReduxState) => state.brands.directFulfillmentBrands);
  const allStoreProducts = useSelector((state: IReduxState) => state.products.store_products.catalogProducts);
  const {loadingFullfilmentRequest} = useSelector((state: IReduxState) => state.partners);
  const {profile} = useSelector((state: IReduxState) => state.user);
  const userStore = profile?.stores?.[0];

  const permissions = profile?.config?.permissions || {};
  const brand = galleryData.find((i) => i.option_id === params.id);
  const {
    queueTasksSkus,
    store_products,
    brand_products = [],
    productsLoading,
    brandProductsCount,
    loadingBrandProducts,
    storeTaxClasses = []
  } = useSelector((state: IReduxState) => state.products);

  const allQueueTasksSkus = [
    ...queueTasksSkus?.[userStore.quoteProStoreCode] || [],
    ...queueTasksSkus?.[userStore.storeCode] || []
  ];
  const { store_products_sku: storeProductsSku } = store_products;
  const [selectedProducts, setselectedProducts] = useState([]);
  const [searchValue, setSearchValue] = useState('');

  const loading = productsLoading || loadingBrandProducts;

  useEffect(() => {
    if (searchValue.length !== 1) {
      getMasterCatalogProductsApi({dispatch, brand: `${params.id}`, name: searchValue, fullfilledByDermpro: 1, brandType: 'prod'});
    }

    if (galleryData.length === 0) {
      getBrands({dispatch, fullfilledByDermpro: 1, skuLike: 'PROD'});
    }

    getStoreTaxClassesApi({dispatch});
  }, []);

  useEffect(() => {
    if (!allStoreProducts.length && userStore?.isCreated) {
      getStoreProductsApi({dispatch});
    }
  }, [userStore]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const taxableClass = storeTaxClasses.find((c) => c.class_name.toLowerCase().includes(userStore?.storeCode));

    const tasks = selectedProducts.map((product) => {
      const productAttributes = product.custom_attributes.map((item) => {
        if (item.attribute_code === 'tax_class_id') {
          return {...item, value: product.sku.includes('PROD') && taxableClass ? String(taxableClass?.class_id) : '0'};
        }

        return item;
      });
      return {
        sku: product.sku,
        name: product.name,
        body: {
          ...product,
          status: 2,
          custom_attributes: [
            ...productAttributes,
            {
              attribute_code: 'catalog_brand_name',
              value: brand.value
            }
          ]
        },
        requestType: 'CREATE_PRODUCT'
      };
    });

    addTasksToQueueApi({dispatch, tasks});

    history.push('/direct-fulfillment');
  };

  const handleRequestProducts = async () => {
    const requestedProducts = selectedProducts.map((product) => ({
      name: product.name,
      sku: product.sku,
      brand: brand.value
    }));

    const isSuccess = await sendRequestProductsEmailApi({dispatch, requestedProducts});

    if (isSuccess) {
      history.push('/direct-fulfillment');
    }
  };

  const handleChange = (e, product) => {
    const {checked} = e.target;

    if (checked) {
      setselectedProducts([...selectedProducts, product]);
    } else {
      const filteredProducts = selectedProducts.filter((p) => p.sku !== product.sku);
      setselectedProducts(filteredProducts);
    }
  };

  const handleSelectAll = () => {
    setselectedProducts(brand_products.filter((product) => !allQueueTasksSkus.includes(product.sku) && !storeProductsSku?.includes(product.sku)));
  };

  const handleUnselectAll = () => {
    setselectedProducts([]);
  };

  const handleSearch = (value) => {
    if (value.length !== 1) {
      getMasterCatalogProductsApi({dispatch, brand: `${params.id}`, name: value, fullfilledByDermpro: 1, brandType: 'prod'});
      setselectedProducts([]);
    }
  };

  return (
    <div className="flex-grow pb-15 h-full min-h-full-screen scroll-y">
      <div className="mb-sm-30">
        {galleryData.length !== 0 ?
          <Breadcrumb
            routeSegments={[
              { name: 'Direct Fulfillment', path: '/direct-fulfillment' },
              { name: `${brand?.value}`}
            ]}
          />
          : <Skeleton component="h4" variant="text" className="mt-8 mx-8 max-w-400" />
        }
      </div>
      <Link to="/direct-fulfillment" className="mx-10 flex items-center"><Icon className="mr-2">assignment_return</Icon>Go back to Brands</Link>
      <div className="m-10">
        <form onSubmit={handleSubmit}>
          <Grid container className="sticky bg-white py-4">
            <Grid item xs={12}>
              {loading
                ? <Skeleton component="h4" variant="text" className="m-8 max-w-400" />
                :
                <div className="flex items-center">
                  <Typography variant="h3" className="p-8">
                    Items from {brand?.value}
                    <span className="text-muted font-medium text-16 pl-2">
                      ({selectedProducts.length > 0 ? <span>{selectedProducts.length} {selectedProducts.length === 1 ? 'item' : 'items'} selected from </span> : ''}
                      {brandProductsCount})
                    </span>
                  </Typography>
                </div>
              }
            </Grid>
            <Grid item xs={12} className="flex justify-between px-8 flex-wrap items-center">
              {loading ?
                null :
                <>
                  <FormControl className="w-full max-w-600 pb-4 pr-14 flex items-center relative">
                    <TextField
                      label="Search item by name"
                      variant="outlined"
                      fullWidth
                      value={searchValue}
                      onChange={(event) => setSearchValue(event.target.value)}
                      id="back-to-top-anchor"
                      onKeyPress={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          handleSearch(searchValue);
                        }
                      }}
                      inputProps={{ className: 'pr-8' }}
                    />
                    {searchValue.length === 1 &&
                      <FormHelperText
                        className="position-bottom"
                        style={{left: '10px'}}
                      >
                        Enter two or more symbols and press Enter or Search icon
                      </FormHelperText>
                    }
                    {!!searchValue.length &&
                    <>
                      <Icon
                        className="text-muted absolute cursor-pointer"
                        fontSize="small"
                        style={{right: '65px', top: '11px'}}
                        onClick={() => {
                          setSearchValue('');
                          handleSearch('');
                        }}
                      >
                        close
                      </Icon>
                      <IconButton
                        className="absolute"
                        title="Search"
                        onClick={() => handleSearch(searchValue)}
                        style={{right: '0', top: '-3px'}}
                      >
                        <Icon>search</Icon>
                      </IconButton>
                    </>
                    }
                  </FormControl>
                  <div className="mb-4">
                    <Button
                      type="button"
                      color="primary"
                      variant="contained"
                      className="uppercase custom-check-all custom-check-all-select mr-2"
                      onClick={handleSelectAll}
                    >
                      <Icon>done_all</Icon>
                      Select All
                    </Button>
                    <Button
                      type="button"
                      color="primary"
                      variant="contained"
                      className="uppercase custom-check-all mr-2"
                      onClick={handleUnselectAll}
                    >
                      <Icon>remove</Icon>
                      Unselect All
                    </Button>
                    {permissions?.directFulfillmentAddButton &&
                      <Button type="submit" disabled={selectedProducts.length < 1} variant="contained" color="primary" className="uppercase mr-2" >
                        <Icon>add</Icon>
                        Add To Store
                      </Button>
                    }
                    {permissions?.directFulfillmentRequestButton &&
                      <Button
                        onClick={() => handleRequestProducts()}
                        disabled={selectedProducts.length < 1}
                        variant="contained"
                        color="primary"
                        className="uppercase"
                      >
                        <Icon>add</Icon>
                        Request
                      </Button>
                    }
                  </div>
                </>
              }
            </Grid>
          </Grid>
          <Card elevation={25} className="p-8">
            <Grid container spacing={3}>
              {loading &&
                <Card elevation={25} className="text-center p-8">
                  <Skeleton variant="rect" width={180} height={160} />
                  <Skeleton variant="text" className="mt-10" />
                  <Skeleton variant="text" />
                  <Skeleton variant="text" className="mt-16 ml-17" width={50} />
                </Card>
              }
              {!loading  && brand_products.length === 0 && searchValue.length > 1 &&
                <Typography variant="body1" className="p-4">No matching items found.</Typography>
              }
              {!loading && brand_products.map((product, idx) => {
                const isSelected = Boolean(selectedProducts?.find((sel) => sel.sku == product.sku));

                return (
                  <Grid item xs={12} sm={3} md={2} key={idx}>
                    <Card
                      elevation={25}
                      className={clsx('text-center relative p-2 pt-8', allQueueTasksSkus.includes(product.sku) || storeProductsSku?.includes(product.sku) ? classes.disabled : null)}
                    >
                      {!allQueueTasksSkus.includes(product.sku) && !storeProductsSku?.includes(product.sku) &&
                      <Checkbox
                        color="primary"
                        style={{position: 'absolute', right: '0', top: '0'}}
                        checked={isSelected}
                        onChange={(e) => handleChange(e, product)}
                      />
                      }
                      <img
                        src={
                          product.custom_attributes.filter((i) => i.attribute_code === 'image').length
                            ? `${imgBaseUrl}${product.custom_attributes.filter((i) => i.attribute_code === 'image').map((i) => i.value)}`
                            : logo}
                        alt={product.name}
                        className={product.custom_attributes.filter((i) => i.attribute_code === 'image').length ? 'max-w-full max-h-140' : 'w-full max-h-140'}
                        onError={(e) => {
                          (e.target as HTMLImageElement).onerror = null;
                          (e.target as HTMLImageElement).src = logo;
                        }}
                      />
                      <Typography variant="h5" className="mt-6 font-normal" style={{minHeight: '130px'}}>{product.name}</Typography>
                      <Typography variant="h5" className="mt-2">
                        {product.price ? `$${Number(product.price).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}` : '-'}
                      </Typography>
                    </Card>
                  </Grid>
                );
              })
              }
            </Grid>
          </Card>
        </form>
      </div>
      <BackdropCircular loading={loadingFullfilmentRequest} />
    </div>
  );
}
